import { useMemo, useState } from "react";
import { useI18n } from "../../i18n";
import { shouldAutoFocusEditable } from "../../lib/mobileFocus";
import type { AgentProfile, ChannelTask, TaskOwnerType, TaskStatus, WorkspaceMember } from "../../types/collab";
import type { TaskPatch } from "../../api/collab";

interface NewTaskInput {
  title: string;
  description?: string;
  ownerType?: TaskOwnerType | null;
  ownerId?: string | null;
}

interface Props {
  tasks: ChannelTask[];
  members: WorkspaceMember[];
  agents: AgentProfile[];
  memberNames: Record<string, string>;
  currentUserId: string;
  onCreate: (input: NewTaskInput) => Promise<void>;
  onUpdate: (taskId: string, patch: TaskPatch) => Promise<void>;
  onOpenThread: (rootId: string) => void;
}

const STATUSES: TaskStatus[] = ["todo", "in_progress", "in_review", "done"];

function ownerValue(type: TaskOwnerType | null, id: string | null): string {
  return type && id ? `${type}|${id}` : "";
}

export function decodeTaskOwner(value: string): { ownerType: TaskOwnerType | null; ownerId: string | null } {
  if (!value) return { ownerType: null, ownerId: null };
  const separator = value.indexOf("|");
  const type = value.slice(0, separator);
  return {
    ownerType: type === "agent" ? "agent" : "human",
    ownerId: value.slice(separator + 1),
  };
}

export function groupTasksByStatus(tasks: ChannelTask[]): Record<TaskStatus, ChannelTask[]> {
  return Object.fromEntries(
    STATUSES.map((status) => [status, tasks.filter((task) => task.status === status)])
  ) as Record<TaskStatus, ChannelTask[]>;
}

export function ChannelTasks({
  tasks,
  members,
  agents,
  memberNames,
  currentUserId,
  onCreate,
  onUpdate,
  onOpenThread,
}: Props) {
  const { t } = useI18n();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [owner, setOwner] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const columns = useMemo(
    () => groupTasksByStatus(tasks),
    [tasks]
  );

  const run = async (id: string, action: () => Promise<void>) => {
    setBusyId(id);
    setError(false);
    try {
      await action();
    } catch {
      setError(true);
    } finally {
      setBusyId(null);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!title.trim()) return;
    await run("new", async () => {
      const selectedOwner = decodeTaskOwner(owner);
      await onCreate({
        title: title.trim(),
        description: description.trim() || undefined,
        ...selectedOwner,
      });
      setTitle("");
      setDescription("");
      setOwner("");
      setCreating(false);
    });
  };

  const transition = (task: ChannelTask, status: TaskStatus) => {
    const patch: TaskPatch = { status };
    if (status === "in_progress" && !task.ownerId && task.status === "todo") {
      patch.ownerType = "human";
      patch.ownerId = currentUserId;
    }
    return run(task.id, () => onUpdate(task.id, patch));
  };

  const renderActions = (task: ChannelTask) => {
    if (task.status === "todo") {
      return <button type="button" className="ws-task-action primary" onClick={() => void transition(task, "in_progress")}>{t("collab.tasks.start")}</button>;
    }
    if (task.status === "in_progress") {
      return <button type="button" className="ws-task-action" onClick={() => void transition(task, "in_review")}>{t("collab.tasks.submitReview")}</button>;
    }
    if (task.status === "in_review") {
      return (
        <>
          <button type="button" className="ws-task-action" onClick={() => void transition(task, "in_progress")}>{t("collab.tasks.return")}</button>
          <button type="button" className="ws-task-action primary" onClick={() => void transition(task, "done")}>{t("collab.tasks.approve")}</button>
        </>
      );
    }
    return <button type="button" className="ws-task-action" onClick={() => void transition(task, "in_review")}>{t("collab.tasks.reopen")}</button>;
  };

  return (
    <div className="ws-task-view">
      <div className="ws-task-toolbar">
        <span className="ws-task-total">{t("collab.tasks.count", { count: String(tasks.length) })}</span>
        <button type="button" className="ws-btn small" onClick={() => setCreating((value) => !value)}>
          {creating ? t("collab.channels.cancel") : t("collab.tasks.new")}
        </button>
      </div>

      {creating ? (
        <form className="ws-task-new" onSubmit={submit}>
          <input className="ws-input" value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t("collab.tasks.titlePlaceholder")} autoFocus={shouldAutoFocusEditable()} />
          <input className="ws-input" value={description} onChange={(event) => setDescription(event.target.value)} placeholder={t("collab.tasks.descriptionPlaceholder")} />
          <select className="ws-input ws-task-owner" value={owner} onChange={(event) => setOwner(event.target.value)} aria-label={t("collab.tasks.owner")}>
            <option value="">{t("collab.tasks.unassigned")}</option>
            <optgroup label={t("collab.members")}>
              {members.map((member) => <option key={member.userId} value={ownerValue("human", member.userId)}>{memberNames[member.userId] || member.userId}</option>)}
            </optgroup>
            <optgroup label={t("collab.tasks.agents")}>
              {agents.map((agent) => <option key={agent.id} value={ownerValue("agent", agent.id)}>@{agent.handle}</option>)}
            </optgroup>
          </select>
          <button type="submit" className="ws-btn primary small" disabled={!title.trim() || busyId === "new"}>{t("collab.channels.create")}</button>
        </form>
      ) : null}
      {error ? <p className="ws-warn">{t("collab.tasks.error")}</p> : null}

      <div className="ws-task-board">
        {STATUSES.map((status) => (
          <section className={`ws-task-column ${status}`} key={status} aria-labelledby={`task-column-${status}`}>
            <header className="ws-task-column-head">
              <h5 id={`task-column-${status}`}>{t(`collab.tasks.status.${status}`)}</h5>
              <span>{columns[status].length}</span>
            </header>
            <div className="ws-task-list">
              {columns[status].length === 0 ? <p className="ws-task-empty">{t("collab.tasks.emptyColumn")}</p> : null}
              {columns[status].map((task) => (
                <article className="ws-task-card" key={task.id} aria-busy={busyId === task.id}>
                  <h6>{task.title}</h6>
                  {task.description && task.description !== task.title ? <p>{task.description}</p> : null}
                  <div className="ws-task-owner-row">
                    <span>{t("collab.tasks.owner")}</span>
                    <select
                      value={ownerValue(task.ownerType, task.ownerId)}
                      onChange={(event) => void run(task.id, () => onUpdate(task.id, decodeTaskOwner(event.target.value)))}
                      aria-label={t("collab.tasks.owner")}
                    >
                      <option value="">{t("collab.tasks.unassigned")}</option>
                      <optgroup label={t("collab.members")}>
                        {members.map((member) => <option key={member.userId} value={ownerValue("human", member.userId)}>{memberNames[member.userId] || member.userId}</option>)}
                      </optgroup>
                      <optgroup label={t("collab.tasks.agents")}>
                        {agents.map((agent) => <option key={agent.id} value={ownerValue("agent", agent.id)}>@{agent.handle}</option>)}
                      </optgroup>
                    </select>
                  </div>
                  <footer className="ws-task-card-actions">
                    {task.threadId ? (
                      <button type="button" className="ws-task-thread" onClick={() => onOpenThread(task.threadId!)} title={t("collab.tasks.openThread")} aria-label={t("collab.tasks.openThread")}>
                        ↩
                      </button>
                    ) : <span />}
                    <div>{renderActions(task)}</div>
                  </footer>
                </article>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
